import {
  Lane,
  LaneApiPayload,
  LaneFormValues,
  LaneGateType,
  LaneStatus,
  LaneTableRow,
  LaneZone,
} from '../models/lane.model';

export const LANE_TRANSACTION_TYPE_OPTIONS = [
  { id: 'cash', name: 'Cash' },
  { id: 'card', name: 'Card Payment' },
  { id: 'link', name: 'Payment Link' },
] as const;

export const ALL_LANE_TRANSACTION_TYPE_IDS: string[] = LANE_TRANSACTION_TYPE_OPTIONS.map(
  option => option.id
);

const TRANSACTION_TYPE_ALIAS: Record<string, string> = {
  cash: 'cash',
  'cash payment': 'cash',
  cash_payment: 'cash',
  card: 'card',
  card_present: 'card',
  'card payment': 'card',
  card_payment: 'card',
  link: 'link',
  'payment link': 'link',
  payment_link: 'link',
  'tap-to-pay': 'card',
  tap_to_pay: 'card',
  taptopay: 'card',
  'tap to pay': 'card',
  terminal: 'card',
};

export function allLaneTransactionTypeIds(): string[] {
  return [...ALL_LANE_TRANSACTION_TYPE_IDS];
}

export function canonicalizeLaneTransactionType(value: unknown): string | null {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();
  return TRANSACTION_TYPE_ALIAS[normalized] ?? null;
}

export function resolveLaneTransactionTypeIds(raw: unknown): string[] {
  if (raw == null || raw === '' || raw === 'all') {
    return allLaneTransactionTypeIds();
  }
  if (!Array.isArray(raw)) {
    return allLaneTransactionTypeIds();
  }
  if (raw.length === 0) {
    return [];
  }
  if (
    raw.some(
      item =>
        String(item ?? '')
          .trim()
          .toLowerCase() === 'all'
    )
  ) {
    return allLaneTransactionTypeIds();
  }

  const unique: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const canonical = canonicalizeLaneTransactionType(item);
    if (!canonical || seen.has(canonical)) {
      continue;
    }
    seen.add(canonical);
    unique.push(canonical);
  }

  return unique.length ? unique : allLaneTransactionTypeIds();
}

export function serializeLaneTransactionTypes(ids: string[]): string[] | null {
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    const canonical = canonicalizeLaneTransactionType(id);
    if (!canonical || seen.has(canonical)) {
      continue;
    }
    seen.add(canonical);
    unique.push(canonical);
  }

  if (unique.length === 0 || unique.length === ALL_LANE_TRANSACTION_TYPE_IDS.length) {
    return unique.length === ALL_LANE_TRANSACTION_TYPE_IDS.length ? null : [];
  }

  return unique;
}

export function areAllLaneTransactionTypesSelected(ids: string[]): boolean {
  const selected = new Set(
    ids.map(id => canonicalizeLaneTransactionType(id)).filter((id): id is string => !!id)
  );
  return ALL_LANE_TRANSACTION_TYPE_IDS.every(id => selected.has(id));
}

function normalizeGateType(value: any): LaneGateType | null {
  const type = String(value ?? '').toLowerCase();
  if (type === 'automatic' || type === 'auto') {
    return 'automatic';
  }
  if (type === 'manual') {
    return 'manual';
  }
  return null;
}

function normalizeStatus(value: any): LaneStatus {
  return String(value ?? '').toLowerCase() === 'inactive' ? 'inactive' : 'active';
}

function readString(raw: Record<string, any>, ...keys: string[]): string {
  for (const key of keys) {
    const value = raw[key];
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
}

export function mapLaneFromApi(raw: Lane | Record<string, any>): LaneTableRow {
  const record = raw as Record<string, any>;
  const gateType = normalizeGateType(record['gate_type'] ?? record['type'] ?? record['lane_type']);
  const cameraSerial = readString(record, 'camera_serial', 'camera_serial_number');
  const roiRaw = record['camera_roi'];
  const roi = roiRaw != null ? Number(roiRaw) : null;
  const camera =
    cameraSerial && (roi === 1 || roi === 2) ? `${cameraSerial} (ROI ${roi})` : cameraSerial || '—';
  const gateSerial = readString(record, 'gate_serial', 'gate_serial_number');

  // Connection status is not carried on the lane payload (CWS-1262). `lanes` has no
  // gate_/camera_last_connected_at column — reading them always yielded undefined, so
  // every device rendered offline. Both are resolved live from the SSE registry by the
  // caller (LaneService.checkDeviceConnection), keyed by serial.
  const cameraConnected = false;
  const gateConnected = false;

  return {
    id: String(record['id'] ?? ''),
    name: readString(record, 'name', 'lane_name') || '—',
    type: gateType,
    camera,
    cameraSerial: cameraSerial || null,
    cameraConnected,
    gate: gateSerial || null,
    gateSerial: gateSerial || null,
    gateConnected,
    status: normalizeStatus(record['status']),
  };
}

export function mapLaneToForm(lane: Lane): LaneFormValues {
  const roi: LaneZone = lane.camera_roi === 2 ? 'type_2' : 'type_1';
  const gateType = lane.gate_type ?? 'automatic';
  const hasCamera =
    lane.camera_serial != null ||
    lane.camera_roi != null ||
    lane.camera_status != null ||
    lane.camera_model != null;

  return {
    laneName: lane.name ?? '',
    hasCamera,
    cameraSerialNumber: lane.camera_serial ?? '',
    cameraModel: lane.camera_model ?? '',
    cameraStatus: normalizeStatus(lane.camera_status),
    zone: roi,
    hasGate: lane.gate_type != null || lane.gate_serial != null || lane.gate_relay != null,
    laneType: gateType,
    gateRelay: lane.gate_relay != null ? String(lane.gate_relay) : DEFAULT_GATE_OPEN_PORT,
    gateSerialNumber: lane.gate_serial ?? '',
    gateModel: lane.gate_model ?? '',
    closeGateEnabled: lane.gate_close_relay != null,
    gateCloseRelay:
      lane.gate_close_relay != null ? String(lane.gate_close_relay) : DEFAULT_GATE_CLOSE_PORT,
    status: normalizeStatus(lane.status),
    transactionTypeIds: resolveLaneTransactionTypeIds(lane.gate_auto_open_transaction_types),
  };
}

export function buildLanePayload(form: LaneFormValues, mode: 'create' | 'update'): LaneApiPayload {
  const payload: LaneApiPayload = {
    name: form.laneName.trim(),
  };

  if (mode === 'update') {
    payload.status = form.status;
  }

  if (form.hasCamera) {
    payload.camera_status = form.cameraStatus;
    const cameraSerial = form.cameraSerialNumber.trim();
    const cameraModel = form.cameraModel.trim();
    payload.camera_model = cameraModel || null;
    if (cameraSerial) {
      payload.camera_serial = cameraSerial;
      payload.camera_roi = form.zone === 'type_2' ? 2 : 1;
    } else {
      payload.camera_serial = null;
      payload.camera_roi = null;
    }
  } else {
    payload.camera_status = null;
    payload.camera_serial = null;
    payload.camera_model = null;
    payload.camera_roi = null;
  }

  if (form.hasGate) {
    payload.gate_type = form.laneType === 'automatic' ? 'automatic' : 'manual';
    payload.gate_serial = form.gateSerialNumber.trim();
    payload.gate_relay = parseGatePort(form.gateRelay);
    const gateModel = form.gateModel.trim();
    payload.gate_model = gateModel || null;
    payload.gate_auto_open_transaction_types =
      form.laneType === 'automatic' ? serializeLaneTransactionTypes(form.transactionTypeIds) : null;
    payload.gate_close_relay = resolveGateCloseRelay(form);
  } else {
    payload.gate_type = null;
    payload.gate_serial = null;
    payload.gate_model = null;
    payload.gate_relay = null;
    payload.gate_params = null;
    payload.gate_close_relay = null;
    payload.gate_auto_open_transaction_types = null;
  }

  return payload;
}

function resolveGateCloseRelay(form: LaneFormValues): number | null {
  if (!form.closeGateEnabled) {
    return null;
  }
  const closePort = parseGatePort(form.gateCloseRelay);
  return closePort !== null && closePort !== parseGatePort(form.gateRelay) ? closePort : null;
}

/**
 * Gate relays are VAPIX port numbers on the gate controller, typed in (CWS-1714):
 * the A9210's own relay is port 1, A9910 expansion relays sit on higher ports.
 * Same range as GATE_RELAY_PORT_MIN/MAX in @car-wash/shared-utils.
 */
export const GATE_RELAY_PORT_MIN = 1;
export const GATE_RELAY_PORT_MAX = 255;
/** Prefilled when a lane has no open relay port yet. */
export const DEFAULT_GATE_OPEN_PORT = '1';
/** Prefilled when closing is turned on for a lane without a close relay port. */
export const DEFAULT_GATE_CLOSE_PORT = '20';

/** The port number typed in, or null when it is not a whole number in range. */
export function parseGatePort(value: string | null | undefined): number | null {
  const text = String(value ?? '').trim();
  if (!/^\d{1,3}$/.test(text)) {
    return null;
  }
  const port = Number(text);
  return port >= GATE_RELAY_PORT_MIN && port <= GATE_RELAY_PORT_MAX ? port : null;
}

/** Validation message for a typed port number, or '' when it is valid. */
export function gatePortError(value: string | null | undefined): string {
  if (!String(value ?? '').trim()) {
    return 'Port number is required';
  }
  return parseGatePort(value) === null
    ? `Enter a whole number from ${GATE_RELAY_PORT_MIN} to ${GATE_RELAY_PORT_MAX}`
    : '';
}

export function parseRelayValue(relay: string): number {
  if (/^[1-4]$/.test(relay)) {
    return Number(relay);
  }
  const match = /^relay_([1-4])$/.exec(relay);
  return match ? Number(match[1]) : Number(relay);
}

export type LaneSaveFieldError =
  'laneName' | 'cameraSerial' | 'gateSerial' | 'gateRelay' | 'gateCloseRelay';

export function mapLaneSaveError(message: string): {
  field: LaneSaveFieldError | null;
  message: string;
} {
  const text = String(message ?? '').trim() || 'Failed to save lane';
  const lower = text.toLowerCase();

  if (lower.includes('lane name already exists')) {
    return { field: 'laneName', message: text };
  }
  if (lower.includes('camera serial') || lower.includes('camera serial and roi')) {
    return { field: 'cameraSerial', message: text };
  }
  if (lower.includes('gate_close_relay')) {
    return { field: 'gateCloseRelay', message: text };
  }
  if (lower.includes('gate_relay') || (lower.includes('gate serial') && lower.includes('relay'))) {
    return { field: 'gateRelay', message: text };
  }
  if (lower.includes('gate serial')) {
    return { field: 'gateSerial', message: text };
  }

  return { field: null, message: text };
}

/** Lanes counted on the Location → Lanes tab badge (CWS-1921). */
export function countActiveLanes(lanes: Pick<LaneTableRow, 'status'>[]): number {
  return lanes.filter(lane => lane.status === 'active').length;
}
